import { ShortClientResponse } from '@/modules/clients/types/dto/client-short.dto';
import { OpsiumUserShortResponse } from '@/modules/common/types/dto/response/opsium-user-response';
import { ShortProjectResponse } from '@/modules/projects/types/dto/response/short-project-response';

import { BillEntityTypeDTO } from '../bill-entity-type.dto';
import { BillStatusResponse } from '../bill-status.dto';
import { BillSyncOperationResponse } from '../bill-sync-operation.dto';
import { BillMonthlySalaryAvailabilityResponse } from './bill-monthly-salary-availability.response';
import { BillTimeOffTypeSourceResponse } from './bill-time-off-type-source.response';
import { SyncErrorResponse } from './sync-error.response';

export enum BillSyncStatusResponse {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  SYNCHRONIZED = 'SYNCHRONIZED',
  FAILED = 'FAILED',
}

export enum BillAllowedActionResponse {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  RETRY = 'RETRY',
}

interface TimeOffTypeShortResponse {
  id: number;
  name: string;
}

interface BillDetailsBaseItemResponse {
  id: number;
  entityType: BillEntityTypeDTO;
  amount: string;
}

export interface BillDetailsProjectItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.PROJECT;
  entityId: number;
  project: ShortProjectResponse | null;
  clientName: string | null;
  quantity: string | null;
  unitPrice: string | null;
}

export interface BillDetailsTimeOffItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.TIME_OFF_TYPE;
  entityId: number;
  timeOffType: TimeOffTypeShortResponse | null;
  quantity: string | null;
  unitPrice: string | null;
}

export interface BillDetailsSalaryItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.SALARY;
  quantity: string | null;
  unitPrice: string | null;
}

export interface BillDetailsClientItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.CLIENT;
  entityId: number;
  client: ShortClientResponse | null;
  description: string | null;
  quantity: string | null;
  unitPrice: string | null;
}

export interface BillDetailsUnlinkedItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.NONE;
  description: string | null;
  quantity: string | null;
  unitPrice: string | null;
}

export interface BillDetailsFlatAmountItemResponse extends BillDetailsBaseItemResponse {
  entityType: BillEntityTypeDTO.OVERHEAD | BillEntityTypeDTO.ADJUSTMENT;
  description: string | null;
}

export type BillDetailsItemResponse =
  | BillDetailsProjectItemResponse
  | BillDetailsTimeOffItemResponse
  | BillDetailsSalaryItemResponse
  | BillDetailsClientItemResponse
  | BillDetailsUnlinkedItemResponse
  | BillDetailsFlatAmountItemResponse;

/** Raw GET /api/bills/:billId response. Keep this DTO at the API boundary. */
export interface BillDetailsResponse {
  id: number;
  externalId: string | null;
  referenceNumber: string | null;
  status: BillStatusResponse;
  syncStatus: BillSyncStatusResponse;
  syncOperation: BillSyncOperationResponse | null;
  syncError: SyncErrorResponse | null;
  syncToken: string;
  allowedActions: BillAllowedActionResponse[];
  member: OpsiumUserShortResponse;
  startDate: string | null;
  endDate: string | null;
  dueDate: string;
  currencyCode: string;
  totalAmount: string;
  balance: string;
  amountPaid: string;
  monthlySalaryAvailability: BillMonthlySalaryAvailabilityResponse;
  timeOffSources: BillTimeOffTypeSourceResponse[];
  createdAt: string;
  items: BillDetailsItemResponse[];
}
